import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, IndianRupee, Users } from 'lucide-react';
import { SalesKPIs } from '@/types/salesDashboard';

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Animated Counter Component
const AnimatedCounter: React.FC<{
  value: number;
  format?: 'currency' | 'number';
}> = ({ value, format = 'number' }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 1000;
    const steps = 30;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  if (format === 'currency') {
    return <>{formatCurrency(displayValue)}</>;
  }
  return <>{displayValue.toLocaleString('en-IN')}</>;
};

interface KPICardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  gradient: string;
  format: 'currency' | 'number';
  delay?: number;
  onClick?: () => void;
  tooltip?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  label,
  value,
  icon: Icon,
  gradient,
  format,
  delay = 0,
  onClick,
  tooltip,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={`rounded-xl p-5 bg-gradient-to-br ${gradient} shadow-lg hover:shadow-xl transition-shadow duration-300 ${onClick ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      aria-label={onClick ? `${label}: ${format === 'currency' ? formatCurrency(value) : value}. Click to view details.` : undefined}
      title={tooltip}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-white/80 mb-1">{label}</p>
          <p className="text-2xl font-bold text-white">
            <AnimatedCounter value={value} format={format} />
          </p>
        </div>
        <div className="p-3 bg-white/20 rounded-lg">
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
};

interface SalesKPICardsProps {
  kpis: SalesKPIs;
  isLoading?: boolean;
  onCardClick?: (key: string) => void;
}

export const SalesKPICards: React.FC<SalesKPICardsProps> = ({
  kpis,
  isLoading,
  onCardClick,
}) => {
  const cards = [
    {
      key: 'monthlyTarget',
      label: 'Monthly Target',
      value: kpis.monthlyTarget,
      icon: Target,
      gradient: 'from-red-500 to-red-600',
      format: 'currency' as const,
    },
    {
      key: 'totalSales',
      label: 'Bookings (MTD)',
      value: kpis.totalSales,
      icon: TrendingUp,
      gradient: 'from-orange-500 to-orange-600',
      format: 'currency' as const,
      tooltip: 'Sum of deal values for leads WON this month. Measures commitment, not cash.',
    },
    {
      key: 'netRevenue',
      label: 'Collections (MTD)',
      value: kpis.netRevenue,
      icon: IndianRupee,
      gradient: 'from-green-500 to-green-600',
      format: 'currency' as const,
      tooltip: 'Cash received this month from all paid invoices, regardless of when the deal was won.',
    },
    {
      key: 'activePipeline',
      label: 'Active Pipeline',
      value: kpis.activePipeline,
      icon: Users,
      gradient: 'from-yellow-500 to-yellow-600',
      format: 'number' as const,
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="rounded-xl p-5 bg-gray-200 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, index) => (
        <KPICard
          key={card.key}
          label={card.label}
          value={card.value}
          icon={card.icon}
          gradient={card.gradient}
          format={card.format}
          delay={index * 0.1}
          onClick={onCardClick ? () => onCardClick(card.key) : undefined}
          tooltip={(card as { tooltip?: string }).tooltip}
        />
      ))}
    </div>
  );
};
